import React, { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTimezone } from '../../contexts/TimezoneContext';
import type {
  GhostTransactionAnalysis,
  GhostDirectionalAnalysis,
  MetaAnalysisLite,
} from './types';
import {
  formatMoney,
  formatGap,
  formatZScore,
  severityStyle,
  getEffectiveIATStats,
  effectiveElapsedSeconds,
} from './format';

interface GhostAnalysisPanelProps {
  ghost: GhostTransactionAnalysis;
  meta?: MetaAnalysisLite | null;
}

interface GhostRowProps {
  caption: string;
  expandLabel: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  detail: React.ReactNode;
}

const GhostRow: React.FC<GhostRowProps> = ({
  caption,
  expandLabel,
  open,
  onToggle,
  children,
  detail,
}) => (
  <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
    <div className="px-3 pt-2.5 pb-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {caption}
      </div>
      {children}
    </div>
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-1.5 border-t border-border bg-muted/20 px-3 py-1.5 text-left text-[11px] font-semibold text-info hover:bg-muted/40"
    >
      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      <span>{expandLabel}</span>
    </button>
    {open && (
      <div className="border-t border-border px-3 py-2.5 text-[11.5px] leading-relaxed text-muted-foreground">
        {detail}
      </div>
    )}
  </div>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="rounded bg-muted px-1 py-px font-mono text-[11px] text-foreground">{children}</code>
);

const KV: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({ rows }) => (
  <dl className="my-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
    {rows.map(([k, v], i) => (
      <React.Fragment key={i}>
        <dt className="text-muted-foreground">{k}</dt>
        <dd className="m-0 font-medium text-foreground">{v}</dd>
      </React.Fragment>
    ))}
  </dl>
);

/** Tiny inline bar row for the bidirectional 8-week activity evidence. */
const MiniBars: React.FC<{ series: number[] }> = ({ series }) => {
  const max = Math.max(...series, 1);
  return (
    <div className="flex items-end gap-0.5" style={{ height: 22 }}>
      {series.map((v, i) => (
        <div
          key={i}
          title={String(v)}
          className={clsx(
            'flex-1 rounded-t-sm',
            v === 0 ? 'border border-border bg-muted' : 'bg-primary/50',
          )}
          style={{ height: v === 0 ? 5 : Math.max(6, (v / max) * 22) }}
        />
      ))}
    </div>
  );
};

const GhostAnalysisPanel: React.FC<GhostAnalysisPanelProps> = ({ ghost, meta }) => {
  const { formatDateTime } = useTimezone();
  const [openImpact, setOpenImpact] = useState(false);
  const [openZ, setOpenZ] = useState(false);
  const [openCmp, setOpenCmp] = useState(false);

  const z = ghost.absenceZScore ?? null;
  const severity = ghost.absenceSeverityLevel ?? null;
  const sev = severityStyle(severity);
  const stats = getEffectiveIATStats(ghost, meta);
  const elapsed = effectiveElapsedSeconds(ghost);

  const finalAssessment = ghost.historicalActivityAnalysis?.finalAssessment;
  const isHistoricallyClosed =
    z == null && finalAssessment?.assessmentResult === 'Historically Closed';

  // Calm state: quiet as expected — no alarm, no severity color.
  if (isHistoricallyClosed) {
    return (
      <section>
        <div className="mb-2 flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
          <span className="text-primary">A</span> Ghost Transaction
        </div>
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          Quiet, as expected — historically closed at this hour.
          {finalAssessment?.assessmentReason && (
            <span className="mt-1 block text-[11.5px]">{finalAssessment.assessmentReason}</span>
          )}
        </div>
      </section>
    );
  }

  // Open but no statistical read.
  const noRead = z == null;

  const raw = ghost.secondsSinceLastTransaction ?? null;
  const dataDelayAdj = ghost.secondsSinceLastTransactionAdjustedForDataDelay ?? null;
  const dataAndAnalysisAdj =
    ghost.secondsSinceLastTransactionAdjustedForDataAndAnalysisDelay ?? null;
  const elapsedDiffersFromRaw = raw != null && raw !== elapsed;

  const cyc = ghost.cyclicalityAdjustedStatistics;
  const iatStats = ghost.interArrivalTimeStatistics;

  const backward = ghost.historicalActivityAnalysis?.backwardLookingAnalysis ?? null;
  const forward = ghost.historicalActivityAnalysis?.forwardLookingAnalysis ?? null;

  const bucketSeries = (dir: GhostDirectionalAnalysis | null): number[] =>
    (dir?.historicalBuckets ?? []).map((b) => b.transactionCount);

  // --- Impact ---
  const impactValue = ghost.estimatedMissingValueInCurrency ?? null;
  const median = ghost.medianDataPointValueLast30Days ?? null;

  const impactValueNode = impactValue != null ? (
    <span className={sev.text}>
      {formatMoney(impactValue)}{' '}
      <span className="text-[11px] font-semibold">(est.)</span>
    </span>
  ) : (
    <span className="text-muted-foreground">No estimated impact</span>
  );

  const impactDetail =
    impactValue != null ? (
      <>
        <Mono>(duration ÷ expected-gap) × median value × severity multiplier</Mono>
        <KV
          rows={[
            ['duration of silence', `${Math.round(elapsed)}s (adjusted)`],
            ['÷ expected gap', `${Math.round(stats.mean)}s`],
            ['× median value 30d', median != null ? formatMoney(median) : '—'],
            ['× severity mult', <Mono key="m">1 + log2(z)</Mono>],
            ['= estimate', <b key="e">{formatMoney(impactValue)}</b>],
          ]}
        />
        Populated only when historically open AND alert (Caution+) AND a master metric / 30-day
        median exists.
      </>
    ) : (
      <>
        <Mono>estimatedMissingValueInCurrency = null</Mono>. The estimate is computed only when the
        business is historically open AND severity is Caution+ AND a 30-day median exists. A null
        figure is shown as text — never a fabricated <Mono>$0</Mono>.
        {median != null && (
          <div className="mt-1.5">
            Median data point value (30d): <b>{formatMoney(median)}</b>.
          </div>
        )}
      </>
    );

  // --- Z-score ---
  const zValueNode = noRead ? (
    <span className="text-muted-foreground">No statistical read</span>
  ) : (
    <span className={sev.text}>{formatZScore(z)}</span>
  );

  const zDetail = (
    <>
      <Mono>z = (adjusted elapsed − expected mean) / expected stdDev</Mono>
      {noRead ? (
        <div className="mt-1.5">
          Open, but not enough recent history for a statistical read ({stats.label}).
        </div>
      ) : (
        <KV
          rows={[
            ['adjusted elapsed', `${Math.round(elapsed)}s`],
            ['− expected mean', `${Math.round(stats.mean)}s`],
            ['÷ expected stdDev', `${Math.round(stats.stdDev)}s`],
            ['= z', <b key="z">{formatZScore(z)}</b>],
          ]}
        />
      )}
      <div className="mt-1">
        Ghost thresholds are one-sided (≥): <b>2</b> Caution · <b>3</b> Dangerous · <b>4</b> ALERT!.
        Only long gaps trip ghost.
      </div>
    </>
  );

  // --- Comparison table ---
  const histGapLabel = stats.source === 'none' ? '—' : formatGap(stats.mean);

  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
        <span className="text-primary">A</span> Ghost Transaction — is the heartbeat stopped right
        now?
      </div>

      <div className="grid gap-2.5 md:grid-cols-[1.05fr_0.7fr_1.55fr]">
        {/* 1 — impact */}
        <GhostRow
          caption="Estimated impact"
          expandLabel={impactValue != null ? 'How was the estimate built?' : 'Why is there no $ figure?'}
          open={openImpact}
          onToggle={() => setOpenImpact((v) => !v)}
          detail={impactDetail}
        >
          <div className="mt-1 text-lg font-bold tracking-tight">{impactValueNode}</div>
          {median != null && impactValue != null && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              during the silence · median {formatMoney(median)}/30d
            </div>
          )}
        </GhostRow>

        {/* 2 — z-score */}
        <GhostRow
          caption="Absence z-score"
          expandLabel="How is the z-score computed?"
          open={openZ}
          onToggle={() => setOpenZ((v) => !v)}
          detail={zDetail}
        >
          <div className="mt-1 text-lg font-bold tracking-tight">{zValueNode}</div>
          {!noRead && severity && (
            <div className={clsx('mt-0.5 text-[11px] font-semibold', sev.text)}>
              {severity}
              {severity !== 'Normal' && ' · alert'}
            </div>
          )}
        </GhostRow>

        {/* 3 — comparison table */}
        <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className="w-1/2 px-3 py-2 align-top">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Time since last transaction
                  </div>
                  <div className={clsx('mt-0.5 text-base font-bold', noRead ? '' : sev.text)}>
                    {raw != null ? formatGap(raw) : '—'}
                  </div>
                  {elapsedDiffersFromRaw && (
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                      {formatGap(elapsed)} adjusted · the value the z uses
                    </div>
                  )}
                </td>
                <td className="w-1/2 border-l border-border px-3 py-2 align-top">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Avg historical gap
                  </div>
                  <div className="mt-0.5 text-base font-bold text-foreground">{histGapLabel}</div>
                  <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                    {stats.source !== 'none'
                      ? `${formatGap(stats.mean)} ± ${formatGap(stats.stdDev)} · ${stats.label}`
                      : stats.label}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            type="button"
            onClick={() => setOpenCmp((v) => !v)}
            className="flex w-full items-center gap-1.5 border-t border-border bg-muted/20 px-3 py-1.5 text-left text-[11px] font-semibold text-info hover:bg-muted/40"
          >
            {openCmp ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <span>Full ghost fine print — timing, delay-adjustment, basis, gate</span>
          </button>
          {openCmp && (
            <div className="border-t border-border px-3 py-2.5 text-[11.5px] leading-relaxed text-muted-foreground">
              <b className="text-foreground">A → B → C timing chain</b>
              <KV
                rows={[
                  [
                    'A · analysis (now)',
                    ghost.analysisTimestamp ? formatDateTime(ghost.analysisTimestamp) : '—',
                  ],
                  [
                    'B · last sync',
                    ghost.lastSyncAt ? formatDateTime(ghost.lastSyncAt) : '— (live source)',
                  ],
                  [
                    'C · last transaction',
                    ghost.lastTransactionTimestamp
                      ? formatDateTime(ghost.lastTransactionTimestamp)
                      : '—',
                  ],
                ]}
              />

              <div className="mt-2">
                <b className="text-foreground">Delay-adjustment chain</b>
                <div className="mt-1">
                  <Mono>
                    raw {raw != null ? `${Math.round(raw)}s` : '—'} ≥ data-delay{' '}
                    {dataDelayAdj != null ? `${Math.round(dataDelayAdj)}s` : '—'} ≥ data+analysis{' '}
                    {dataAndAnalysisAdj != null ? `${Math.round(dataAndAnalysisAdj)}s` : '—'}
                  </Mono>
                </div>
                <div className="mt-1">
                  → fed the z-score: <b className="text-foreground">{Math.round(elapsed)}s</b>
                </div>
              </div>

              <div className="mt-2">
                <b className="text-foreground">Statistical basis</b>
                <div className="mt-0.5">
                  {cyc
                    ? `${cyc.description ?? cyc.cyclicalityAdjustment} · ${cyc.sampleCount ?? 0} samples · ${cyc.lookbackDays ?? 30}-day lookback`
                    : iatStats?.description ?? '30-day inter-arrival statistics'}
                </div>
                {iatStats && (
                  <div className="mt-0.5 text-[11px]">
                    Ref (30-day): {formatGap(iatStats.normalizedMeanSeconds ?? 0)} ±{' '}
                    {formatGap(iatStats.standardDeviationSeconds ?? 0)}.
                  </div>
                )}
              </div>

              <div className="mt-2">
                <b className="text-foreground">Methodology:</b>{' '}
                <Mono>{ghost.absenceZScoreMethodology ?? 'n/a'}</Mono>
              </div>

              {finalAssessment && (
                <div className="mt-2 border-t border-border pt-2">
                  <b className="text-foreground">Open / closed gate:</b>{' '}
                  {finalAssessment.assessmentResult ?? 'Unknown'}
                  {finalAssessment.assessmentReason && (
                    <div className="mt-0.5 text-[11px]">{finalAssessment.assessmentReason}</div>
                  )}
                  {(backward || forward) && (
                    <div className="mt-2 grid grid-cols-2 gap-2.5">
                      {backward && (
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Backward 8-week · avg{' '}
                            {(backward.averageHistoricalTransactionCount ?? 0).toFixed(2)}
                          </div>
                          <MiniBars series={bucketSeries(backward)} />
                          <div className="mt-0.5 text-[10px]">
                            threshold {(backward.activityThreshold ?? 0).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {forward && (
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Forward 8-week · avg{' '}
                            {(forward.averageHistoricalTransactionCount ?? 0).toFixed(2)}
                          </div>
                          <MiniBars series={bucketSeries(forward)} />
                          <div className="mt-0.5 text-[10px]">
                            threshold {(forward.activityThreshold ?? 0).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GhostAnalysisPanel;
